/* eslint-disable react-hooks/exhaustive-deps */
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react'
import { DateTime } from 'luxon'
import { Fragment, useEffect, useState } from 'react'
import { Tooltip } from 'react-tooltip'

import AlertIcon from '@Icons/alert-triangle-black.svg?react'
import ArrowLeft from '@Icons/arrow-left.svg?react'
import ArrowRight from '@Icons/arrow-right.svg?react'
import ButtonDown300 from '@Icons/button-down-icon-300.svg?react'
import ButtonUp200 from '@Icons/button-up-icon-200.svg?react'
import FileIcon from '@Icons/file.svg?react'
import InfoIcon from '@Icons/info-circle-small.svg?react'
import { useDownloadImportedFileMutation } from '@Store/api/product'
import type { PaginatedQueryParamsWithCachePreference } from '@Store/types'
import { showErrorMessage } from '@Utils/toast/toast-message'

import { getBatchImportMessage } from '../../constants/messages'
import recentImportsColumns from '../../constants/recentImportsColumns'

function RecentImportsTable({
  isLoading,
  data,
  meta,
  onPaginationChange,
  type,
}: {
  isLoading: boolean
  data: any
  meta: any
  onPaginationChange: (
    pagination: PaginatedQueryParamsWithCachePreference,
    preferCache: boolean,
  ) => void
  type: ('apt' | 'upt' | 'upt-c' | 'conveyor')[]
}): JSX.Element {
  const [pagination, setPagination] =
    useState<PaginatedQueryParamsWithCachePreference>({
      page: 1,
      limit: 5,
      orderBy: 'date_created',
      orderDirection: 'desc',
      preferCache: true,
    })

  const [downloadFile, { isError: isDownloadError, error: downloadError }] =
    useDownloadImportedFileMutation()

  useEffect(() => {
    const fetchPagination = { ...pagination }
    delete fetchPagination.preferCache

    onPaginationChange(
      {
        ...fetchPagination,
        offset: (pagination.page - 1) * pagination.limit,
      },
      !!pagination?.preferCache,
    )
  }, [pagination, type])

  useEffect(() => {
    if (isDownloadError) {
      showErrorMessage({
        message:
          (downloadError as any)?.data?.message ||
          'Failed to download the file.',
      })
    }
  }, [isDownloadError, downloadError])

  if (!data || data?.length === 0) {
    return <></>
  }

  return (
    <div className="flex flex-col pb-8 mt-8 w-full">
      <h1 className="pb-4">Recent Imports</h1>
      <div className="relative shadow sm:rounded-lg border border-solid border-gray-200">
        <table className="w-full text-left text-gray-500 table-fixed">
          <thead className="font-medium text-xs border-b">
            <tr>
              {recentImportsColumns.map((col) => {
                const hasTooltip = col === 'Added/Edited'

                return (
                  <th
                    key={col}
                    scope="col"
                    className="px-4 py-3 whitespace-nowrap"
                  >
                    {col}
                    {hasTooltip && (
                      <span data-tooltip-id={`recent-imports-added-tooltip`}>
                        <InfoIcon className="inline ml-0.5 cursor-pointer align-text-bottom h-[1.33em] [&>path]:stroke-gray-500" />
                        <Tooltip
                          id={`recent-imports-added-tooltip`}
                          style={{ pointerEvents: 'auto', zIndex: 9 }}
                          positionStrategy="fixed"
                        >
                          <div className="flex flex-col gap-y-4 w-60 whitespace-normal">
                            <p>
                              Added means that how many products or urls added
                              to your panel.
                            </p>
                            <p>
                              Edited means how many products affected by this
                              import.
                            </p>
                            <a
                              href="https://helpcenter.prisync.com/hc/en-us/articles/360003285233-Importing-New-Products-and-URLs-in-Bulk"
                              target="_blank"
                              className="underline"
                            >
                              More info
                            </a>
                          </div>
                        </Tooltip>
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="text-sm">
            {isLoading ? (
              <tr>
                {recentImportsColumns.map(() => (
                  <td>
                    <div className="animate-pulse bg-gray-200 h-10 mx-2 my-2 rounded-md"></div>
                  </td>
                ))}
              </tr>
            ) : (
              data.map((row: any, i: number) => (
                <tr
                  key={row.id}
                  className={`${
                    i % 2 === 0 ? 'bg-gray-50' : 'bg-white-200'
                  } border-b`}
                >
                  <td className="px-4 py-4 font-medium text-gray-900 break-words">
                    {row?.uploaded_file_upload
                      ? row?.uploaded_file_upload?.originalFileName
                      : row.uploaded_file_location?.split('/').pop()}
                  </td>
                  <td className="pl-4 px-2">
                    {row.operation === 'UPT-C'
                      ? 'Add Competitor URLs'
                      : row.operation === 'UPT'
                        ? 'Edit Products'
                        : row.operation === 'APT'
                          ? 'Add Products'
                          : row.operation === 'CONVEYOR'
                            ? 'Feed'
                            : ''}
                  </td>
                  <td className="px-2 py-4">
                    {DateTime.fromISO(
                      row.date_updated ? row.date_updated : row.date_created,
                    ).toFormat('yyyy-MM-dd HH:mm:ss')}
                  </td>
                  <td className="px-2 py-4 text-center">
                    {row.result_added}/{row.result_edited}
                  </td>
                  <td className="px-2 py-4 text-center">
                    {row.result_processed}
                  </td>
                  <td className="px-2 py-4 text-center">{row.result_error}</td>
                  <td className="px-2 py-4">
                    {getBatchImportMessage(row.current_state)}
                  </td>
                  <td className="px-2 py-4">
                    <Menu>
                      {({ open }) => (
                        <div className="relative">
                          <MenuButton className="group flex flex-row items-center justify-center px-2 py-2.5 border border-solid border-gray-300 rounded-lg bg-white w-full text-gray-700">
                            <span className="pr-3">Download</span>
                            {open ? <ButtonUp200 /> : <ButtonDown300 />}
                          </MenuButton>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <MenuItems
                              className={`absolute right-0 ${i >= data?.length - 2 ? 'bottom-full' : 'top-full'} w-44 my-2 origin-top-right divide-y border border-solid border-gray-100 divide-gray-200 rounded-md bg-white-100 shadow-lg z-10`}
                            >
                              <div className="px-1 py-1 ">
                                <MenuItem>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await downloadFile({
                                          type: 'original',
                                          id: row.id,
                                        })
                                      } catch (error) {
                                        console.log(error)
                                      }
                                    }}
                                    className="text-gray-900 group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-gray-50"
                                  >
                                    <FileIcon />
                                    <span className="font-normal text-sm text-gray-700 pl-3">
                                      Import File
                                    </span>
                                  </button>
                                </MenuItem>
                                {row.result_error > 0 ? (
                                  <MenuItem>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          await downloadFile({
                                            type: 'error',
                                            id: row.id,
                                          })
                                        } catch (error) {
                                          console.log(error)
                                        }
                                      }}
                                      className=" text-gray-900 group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-gray-50"
                                    >
                                      <AlertIcon />
                                      <span className="font-normal text-sm text-gray-700 pl-3">
                                        Errors/Warnings
                                      </span>
                                    </button>
                                  </MenuItem>
                                ) : null}
                              </div>
                            </MenuItems>
                          </Transition>
                        </div>
                      )}
                    </Menu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {data && meta?.totalItems <= pagination?.limit ? (
          <></>
        ) : (
          <div className="flex flex-row justify-between container mx-auto px-6 pt-3 pb-4">
            <button
              type="button"
              disabled={!meta?.hasPreviousPage}
              onClick={() => {
                if (meta?.hasPreviousPage) {
                  setPagination({
                    ...pagination,
                    page: pagination.page - 1,
                  })
                }
              }}
              className="flex items-center justify-center gap-x-2 px-2 border border-solid border-gray-300 disabled:opacity-65 rounded-lg w-18 h-9"
            >
              <ArrowLeft />
              <span className="font-medium text-sm text-gray-900">
                Previous
              </span>
            </button>
            <span className="font-medium text-sm text-gray-900 pt-2">
              Page {pagination.page} of {meta?.totalPages}
            </span>
            <button
              type="button"
              disabled={!meta?.hasNextPage}
              onClick={() => {
                if (meta?.hasNextPage) {
                  setPagination({
                    ...pagination,
                    page: pagination.page + 1,
                  })
                }
              }}
              className="flex items-center justify-center gap-x-2 px-2 border border-solid border-gray-300 disabled:opacity-65 rounded-lg w-18 h-9"
            >
              <span className="font-medium text-sm text-gray-900">Next</span>
              <ArrowRight />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default RecentImportsTable
